import { parseSender } from '~common/utils/parse-sender'
import type {
    GmailMessageAttachmentDetails,
    GmailFullMessageResponse,
    GmailMessagePayloadPart,
    ImporterQueryDateRange,
    GmailMessageParsed,
} from './types'

export class CachedDriveFolderError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'CachedDriveFolderError'
    }
}

export function buildGmailQuery(
    term: string,
    dateRange?: ImporterQueryDateRange,
    searchSubjectOnly?: boolean,
): string {
    let termQuery = term.replace(/ OR /g, ' ').replace(/ /g, ' OR ')
    const [afterExpr, beforeExpr] = dateRange
        ? getDateRangeClauses(dateRange)
        : [null, null]
    const termExpr = searchSubjectOnly ? `subject:(${termQuery})` : termQuery
    return [termExpr, afterExpr, beforeExpr].filter(Boolean).join(' ')
}

function getDateRangeClauses(
    dateRange: ImporterQueryDateRange,
): [string | null, string | null] {
    const startSeconds = Math.floor(dateRange.startMs / 1000)
    const endSeconds = Math.floor(dateRange.endMs / 1000)

    const afterExpr = `after:${startSeconds}`
    const beforeExpr = `before:${endSeconds}`
    return [afterExpr, beforeExpr]
}

export function buildDateRange(
    after: string | null,
    before: string | null,
): ImporterQueryDateRange {
    const startMs = after ? parseDateToUtcStartMs(after) : undefined
    const endMs = before ? parseDateToUtcStartMs(before) : undefined
    if (startMs != null || endMs != null) {
        return {
            startMs: startMs ?? 0,
            endMs: endMs ?? Date.now(),
        }
    }
    // No constraints
    return {
        startMs: 0,
        endMs: Date.now(),
    }
}

function parseDateToUtcStartMs(dateStr: string): number {
    const [y, m, d] = dateStr.split('-').map((s) => parseInt(s, 10))
    return Date.UTC(y, m - 1, d)
}

function findAttachmentsInParts(
    parts: GmailMessagePayloadPart[],
): GmailMessageAttachmentDetails[] {
    const attachments: GmailMessageAttachmentDetails[] = []

    for (const part of parts) {
        if (part && typeof part === 'object') {
            // Check if this part is an attachment
            if (part.filename && part.body && part.body.attachmentId) {
                attachments.push({
                    id: part.body.attachmentId,
                    filename: part.filename,
                    mimeType: part.mimeType,
                    size: part.body.size,
                } as GmailMessageAttachmentDetails)
            }

            // Recursively check nested parts
            if (Array.isArray(part.parts)) {
                attachments.push(...findAttachmentsInParts(part.parts))
            }
        }
    }

    return attachments
}

export function extractAttachmentDetailsFromMessage(
    message: GmailFullMessageResponse,
): GmailMessageAttachmentDetails[] {
    if (!message.payload?.parts) return []

    return findAttachmentsInParts(message.payload.parts)
}

function findHtmlInMessageParts(
    parts: GmailMessagePayloadPart[],
): string | null {
    const htmlParts: Array<{ size: number; html: string }> = []

    for (const part of parts) {
        if (part && typeof part === 'object') {
            if (
                part.mimeType === 'text/html' &&
                part.body &&
                typeof part.body.data === 'string' &&
                part.body.data.length > 0
            ) {
                const base64 = part.body.data
                    .replace(/-/g, '+')
                    .replace(/_/g, '/')
                const html = decodeURIComponent(escape(atob(base64)))
                const size = part.body.size ?? html.length
                htmlParts.push({ size, html })
            }
            if (Array.isArray(part.parts)) {
                const html = findHtmlInMessageParts(part.parts)
                if (html) {
                    htmlParts.push({ size: html.length, html })
                }
            }
        }
    }

    if (htmlParts.length === 0) return null

    const largestPart = htmlParts.reduce((prev, current) =>
        current.size > prev.size ? current : prev,
    )
    return largestPart.html
}

export function extractHtmlFromMessage(
    message: GmailFullMessageResponse,
): string | null {
    const p = message.payload
    if (
        p?.mimeType === 'text/html' &&
        p.body &&
        typeof p.body.data === 'string' &&
        p.body.data.length > 0
    ) {
        const base64 = p.body.data.replace(/-/g, '+').replace(/_/g, '/')
        return decodeURIComponent(escape(atob(base64)))
    }

    if (!Array.isArray(p?.parts)) return null
    return findHtmlInMessageParts(p.parts)
}

export function parseFullMessage(
    msg: GmailFullMessageResponse,
): GmailMessageParsed {
    const headers = msg.payload?.headers ?? []
    const getHeader = (name: string) => {
        const found = headers.find(
            (h) => h.name.toLowerCase() === name.toLowerCase(),
        )
        return found?.value ?? ''
    }

    const subject = getHeader('Subject')
    const sender = getHeader('From')
    const receivedDate = getHeader('Date')
    const attachments = extractAttachmentDetailsFromMessage(msg)
    const html = extractHtmlFromMessage(msg)
    const parsedSender = parseSender(sender)

    return {
        externalId: msg.id,
        subject,
        fromName: parsedSender.name,
        fromEmail: parsedSender.email,
        receivedDate: receivedDate
            ? new Date(receivedDate).getTime()
            : new Date().getTime(),
        htmlContent: html,
        snippet: msg.snippet,
        attachments,
        originalMessage: msg,
    }
}
